/**
 * §18.7 — the Form 940 worksheet: the numbers to copy onto the year's FUTA
 * return (payroll stage 4).
 *
 * A WORKSHEET, AND IT SAYS SO FIRST, as the Form 941, TWC and W-2/W-3
 * worksheets do. The lines are GET /payroll/forms/940's
 * (apps/api/src/payroll/form-940.ts, from the Instructions for Form 940
 * (2025)); this screen lays them out part by part in the form's own words, and
 * files nothing.
 *
 * A ZERO IS "LEAVE BLANK", as the instructions ask: "If you have a line with
 * the value of zero, leave it blank."
 *
 * WHAT MAKES THE RETURN NOT FINAL COMES FIRST: the year not over, a draft run
 * on no line, Texas tax paid late -- line 10 then applies, and this worksheet
 * does not figure it -- and posted entries holding a different FUTA figure from
 * line 12, which is how production's $42.00 that nobody accrued is found.
 * Posted, as the form's runs are: a draft run is on neither side, and while
 * one waits the screen never offers box c, "No payments to employees" (round
 * 6's R6-1).
 *
 * PART 5 GIVES JOINED QUARTERS ONE LINE, and says why: opening totals are one
 * figure for the stretch they cover.
 *
 * The year is kept in the address and chosen from the server's years, as the
 * W-2/W-3 worksheet's is.
 */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type Form940Quarter, type Form940Report } from '../api.js';
import { payCounted } from '../lib/labels.js';
import { formatCents } from '../lib/money.js';

/** Form 940's line 14: "If line 14 is more than $500, you must deposit your tax." */
const DEPOSIT_OVER_CENTS = 50_000;

const ORDINAL = ['1st', '2nd', '3rd', '4th'] as const;
const STARTS = ['January 1', 'April 1', 'July 1', 'October 1'] as const;
const ENDS = ['March 31', 'June 30', 'September 30', 'December 31'] as const;
const LETTER = ['a', 'b', 'c', 'd'] as const;

/** A line, its words on the form, and what to enter; `amount` right-aligns a figure. */
type Row = [line: string, label: string, entry: ReactNode, amount?: boolean];

function Lines({ label, rows }: { label: string; rows: Row[] }) {
  return (
    <div className="table-scroll">
      <table className="lines" aria-label={label}>
        <thead>
          <tr>
            <th>Line</th>
            <th>On the Form</th>
            <th>Enter</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([line, text, entry, amount]) => (
            <tr key={line}>
              <td>{line}</td>
              <td>{text}</td>
              <td className={amount ? 'num' : undefined}>{entry}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A figure, or "Leave blank" for a zero. */
function amount(line: string, text: string, cents: number): Row {
  return cents === 0 ? [line, text, 'Leave blank'] : [line, text, formatCents(cents), true];
}

/** Part 5's line for a quarter, or for quarters joined: "16a", "16a and 16b", "16a to 16c". */
function quarterRow({ from, to, cents }: Form940Quarter): Row {
  const [first, last] = [from - 1, to - 1];
  const line =
    from === to ? `16${LETTER[first]}` : `16${LETTER[first]} ${to - from === 1 ? 'and' : 'to'} 16${LETTER[last]}`;
  const text =
    from === to
      ? `${ORDINAL[first]} quarter (${STARTS[first]} – ${ENDS[first]})`
      : `${ORDINAL[first]} ${to - from === 1 ? 'and' : 'to'} ${ORDINAL[last]} quarters (${STARTS[first]} – ${ENDS[last]}), together`;
  return amount(line, text, cents);
}

export function Form940Page() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const year = yearParam === null ? undefined : Number(yearParam);
  const [report, setReport] = useState<Form940Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getForm940Worksheet(year)
      .then((result) => {
        if (!live) return;
        setReport(result.report);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not build the worksheet.');
      });
    return () => {
      live = false;
    };
  }, [year]);

  function choose(value: string) {
    const params = new URLSearchParams(searchParams);
    params.set('year', value);
    setSearchParams(params, { replace: true });
  }

  // The year in the address, so the choice on screen is never one year while the page reads another.
  const shown = year ?? report?.year;
  const choices = report === null ? [] : [...new Set([...report.years, ...(shown === undefined ? [] : [shown])])].sort((a, b) => b - a);

  return (
    <section className="form-940">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>
        Form 940 Worksheet{' '}
        <InfoNote label="Where These Come From">
          The Instructions for Form 940 (2025), the edition in force until 2026&apos;s is published, with Schedule A (Form
          940). A 2% shareholder&apos;s health insurance premiums are payments on line 3 and exempt on line 4: Publication
          15-B (2026) excludes them from FUTA wages. Line 8 is line 7 × 0.006, rounded once, so the FUTA the runs booked,
          rounded pay by pay, can differ from it by a few cents. The Department of Labor names each year&apos;s credit
          reduction states in November; if Texas is one, line 2 and Schedule A apply.
        </InfoNote>
      </h1>

      {report !== null && shown !== undefined && (
        <div className="row report-period">
          <label>
            Year{' '}
            <select value={shown} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {report !== null && report.year === shown && <FilledForm940 year={report.year} />}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {error === null && (report === null || report.year !== shown) && <p className="muted">Loading…</p>}
      {error === null && report !== null && report.year === shown && <Worksheet report={report} />}
    </section>
  );
}

/**
 * The IRS's own Form 940, filled by the server (issue #46). A year the
 * application has no checked IRS file for -- 2026 until the IRS publishes its
 * final form, about December -- is refused in a sentence, shown here.
 */
function FilledForm940({ year }: { year: number }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function download() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const file = await api.downloadForm940Pdf(year);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `form-940-${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked later rather than at once, as the Form 941's download is.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      setDownloadError(caught instanceof ApiError ? caught.message : 'Could not fill the form.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      {/* A paragraph, not a flex row: the note's prose wraps under the button instead of squeezing it, with a margin before the notices. */}
      <p>
        <button type="button" disabled={downloading} onClick={() => void download()}>
          {downloading ? 'Filling…' : 'Download Filled Form 940'}
        </button>{' '}
        <InfoNote label="What the Filled Form 940 Leaves to You">
          The IRS&apos;s own Form 940, fetched from irs.gov and filled with this worksheet: the header from Business
          Details in Settings, line 1a, lines 3 to 15a, and Part 5 when line 12 asks for it. The type of return, line
          15&apos;s refund choice and bank details, the designee and the signature are left for you; every field stays
          editable. The form is filled only for a year whose final IRS file this application has checked, so a year
          the IRS has not finished is refused until it publishes the form.
        </InfoNote>
      </p>
      {downloadError && (
        <p role="alert" className="error">
          {downloadError}
        </p>
      )}
    </>
  );
}

function Worksheet({ report }: { report: Form940Report }) {
  const w = report.worksheet;
  const drafts = report.draftRuns.length;
  const unaccrued = w.line12 - report.books.futaAccruedCents;

  return (
    <>
      <p className="notice">
        A worksheet: the figures to copy onto Form 940 for {report.year}, from each employee&apos;s year to date. Nothing
        here files the return or pays anything. It goes to the IRS by {report.due.date}
        {report.due.date !== report.due.statutory && <>, the first business day after {report.due.statutory}</>}, or by{' '}
        {report.dueIfDeposited.date} if every dollar of the year&apos;s FUTA tax was deposited when due.
      </p>

      {!report.yearComplete && (
        <p className="notice notice-warn">
          {report.year} is not over: these are the figures so far, and pay still to come will change them.
        </p>
      )}

      {drafts > 0 && (
        <div className="notice notice-warn">
          {drafts === 1 ? `One run in ${report.year} is` : `${drafts} runs in ${report.year} are`} still a draft and on no
          line:
          <ul className="missed-list">
            {report.draftRuns.map((run) => (
              <li key={run.runId}>
                <Link to={`/payroll/runs/${run.runId}`}>
                  {run.employeeName}, paid {run.payDate}
                </Link>
              </li>
            ))}
          </ul>
          Post {drafts === 1 ? 'it' : 'them'}, then read this worksheet again.
        </div>
      )}

      {report.texas.paidLateCents > 0 && (
        <p className="notice notice-warn">
          {formatCents(report.texas.paidLateCents)} of {report.year}&apos;s Texas unemployment tax was paid after{' '}
          {report.due.date}, Form 940&apos;s due date, so line 10 applies. This worksheet does not figure it: use the
          instructions&apos; Worksheet—Line 10, and add its result to line 12.
        </p>
      )}

      {unaccrued !== 0 && (
        <p className="notice notice-warn">
          Line 12 is {formatCents(w.line12)} of FUTA tax for {report.year}, and posted entries accrued{' '}
          {formatCents(report.books.futaAccruedCents)}.{' '}
          {unaccrued > 0
            ? `${formatCents(unaccrued)} is owed that the books do not show: record it on the FUTA liability account and post it, and the deposit tracker will show when it is due.`
            : `Posted entries hold ${formatCents(-unaccrued)} more than the return owes: find the entry before filing.`}
        </p>
      )}

      {report.employees.length === 0 ? (
        drafts > 0 ? (
          <p className="muted">
            No pay is counted for {report.year} until {drafts === 1 ? 'its draft run is' : 'its draft runs are'} posted.
          </p>
        ) : (
          <p className="muted">
            No pay is counted for {report.year}. A business that paid none checks box c at the top of the form, &ldquo;No
            payments to employees in {report.year}&rdquo;, and signs the return.
          </p>
        )
      ) : (
        <Parts report={report} />
      )}

      <section className="settings-section" aria-labelledby="form-940-evidence">
        <h2 id="form-940-evidence">Where the Figures Come From</h2>
        {report.employees.map((employee) => (
          <p key={employee.employeeId} className="muted">
            {employee.employeeName}: {payCounted(employee)}; {formatCents(employee.taxableCents)} of it taxable for FUTA.
          </p>
        ))}
        {report.deposits.length === 0 ? (
          <p className="muted">Line 13: no FUTA payment has been applied to {report.year} yet.</p>
        ) : (
          <>
            <p className="muted">Line 13 is these payments, as the deposit tracker applied them:</p>
            <ul className="history-list" aria-label="Deposits">
              {report.deposits.map((deposit) => (
                <li key={deposit.headerId}>
                  <Link to={`/journals/${deposit.headerId}`}>
                    {deposit.counterparty ?? 'A payment'} on <span className="date-cell">{deposit.paidOn}</span>
                  </Link>{' '}
                  · {formatCents(deposit.cents)}
                  {!deposit.posted && ' (a draft)'}
                </li>
              ))}
            </ul>
          </>
        )}
        {report.unappliedFutaCents > 0 && (
          <p className="muted">
            {formatCents(report.unappliedFutaCents)} was paid on the FUTA account beyond everything the books accrued, so the
            tracker applied it to no year. If it was a deposit for {report.year}, it belongs on line 13 too.
          </p>
        )}
      </section>
    </>
  );
}

function Parts({ report }: { report: Form940Report }) {
  const w = report.worksheet;
  const texas = report.texas;
  const line10 =
    texas.paidLateCents > 0
      ? "Figure it with the instructions' Worksheet—Line 10: Texas tax was paid late"
      : `Leave blank if all of ${report.year}'s Texas unemployment tax is paid by ${report.due.date}` +
        (texas.unpaidCents > 0 ? `: ${formatCents(texas.unpaidCents)} of it is not paid yet` : '');
  const joined = w.line16.some((line) => line.from !== line.to);
  // The opening totals that join quarters: those running past the first quarter's end.
  const spans = [...new Set(report.employees.map((employee) => employee.openingThrough))].filter(
    (through): through is string => through !== null && through > `${report.year}-03-31`,
  );

  return (
    <>
      <p className="muted">
        At the top of the form: {report.employer}&apos;s name, EIN and address, as on its Forms 941. Leave the Type of
        Return boxes blank unless the return is amended, a successor employer&apos;s, or the business&apos;s last.
      </p>

      <section className="settings-section" aria-labelledby="form-940-part1">
        <h2 id="form-940-part1">Part 1: About Your Return</h2>
        <Lines
          label="Part 1"
          rows={[
            ['1a', 'If you had to pay state unemployment tax in one state only, the state abbreviation', w.line1a],
            ['1b', 'If you had to pay state unemployment tax in more than one state', 'Leave blank'],
            [
              '2',
              'If you paid wages in a state that is subject to credit reduction',
              w.line2 ? 'Check, and complete Schedule A (Form 940)' : "Leave blank: the year's figures give Texas no credit reduction",
            ],
          ]}
        />
      </section>

      <section className="settings-section" aria-labelledby="form-940-part2">
        <h2 id="form-940-part2">Part 2: FUTA Tax Before Adjustments</h2>
        <Lines
          label="Part 2"
          rows={[
            amount('3', 'Total payments to all employees', w.line3),
            amount('4', 'Payments exempt from FUTA tax', w.line4),
            ['4a to 4e', 'Check all that apply', w.line4a ? "Check 4a, Fringe benefits: a 2% shareholder's health insurance" : 'Leave blank'],
            amount('5', 'Total of payments made to each employee in excess of $7,000', w.line5),
            amount('6', 'Subtotal (line 4 + line 5)', w.line6),
            amount('7', 'Total taxable FUTA wages (line 3 – line 6)', w.line7),
            amount('8', 'FUTA tax before adjustments (line 7 × 0.006)', w.line8),
          ]}
        />
      </section>

      <section className="settings-section" aria-labelledby="form-940-part3">
        <h2 id="form-940-part3">Part 3: Adjustments</h2>
        <Lines
          label="Part 3"
          rows={[
            [
              '9',
              'If ALL of the taxable FUTA wages you paid were excluded from state unemployment tax',
              'Leave blank: Texas unemployment tax applies to these wages',
            ],
            ['10', 'If SOME were excluded from state unemployment tax, or ANY of it was paid late', line10],
            amount('11', 'If credit reduction applies, the total from Schedule A (Form 940)', w.line11),
          ]}
        />
      </section>

      <section className="settings-section" aria-labelledby="form-940-part4">
        <h2 id="form-940-part4">Part 4: Balance Due or Overpayment</h2>
        <Lines
          label="Part 4"
          rows={[
            amount('12', 'Total FUTA tax after adjustments (lines 8 + 9 + 10 + 11)', w.line12),
            amount('13', 'FUTA tax deposited for the year', w.line13),
            amount('14', 'Balance due', w.line14),
            amount('15a', 'Overpayment', w.line15a),
          ]}
        />
        {w.line14 > 0 && (
          <p className="muted">
            {w.line14 > DEPOSIT_OVER_CENTS
              ? 'Line 14 is more than $500, so it must be deposited, not paid with the return.'
              : 'Line 14 is $500 or less: it may be paid with the return, or deposited.'}
          </p>
        )}
        {w.line15a > 0 && (
          <p className="muted">On line 15b, check one: apply the overpayment to the next return, or have it refunded.</p>
        )}
      </section>

      <section className="settings-section" aria-labelledby="form-940-part5">
        <h2 id="form-940-part5">Part 5: Liability by Quarter</h2>
        {w.part5 ? (
          <>
            <Lines
              label="Part 5"
              rows={[...w.line16.map(quarterRow), amount('17', 'Total tax liability for the year (must equal line 12)', w.line17)]}
            />
            {joined && (
              <p className="muted">
                Pay entered by hand through {spans.join(' and ')} is one total for the stretch it covers, so the quarters
                it spans are one line here: divide it between them by when the wages were paid.
              </p>
            )}
          </>
        ) : (
          <p className="muted">Leave Part 5 blank: line 12 is $500 or less.</p>
        )}
      </section>
    </>
  );
}
